import { createHmac, timingSafeEqual } from 'node:crypto';

/**
 * Signed-token helpers for the "complete your profile" field-collection form.
 *
 * The email re-processor sends a lead whose delivery was soft-blocked
 * (`under_qualified`) a link to a short form that collects the missing
 * qualification-gate fields. The link carries an HMAC token so the form page
 * (unauthenticated marketing route) can trust that the `leadId` was issued by
 * us and not tampered with, WITHOUT exposing any lead PII in the URL.
 *
 * Reuses the same secret as the outreach opt-out links so no new secret has to
 * be provisioned (`OPT_OUT_HMAC_SECRET`, falling back to `CRON_SECRET`). The
 * purpose string is namespaced so a leaked opt-out token cannot be replayed
 * against this endpoint and vice versa.
 */
const PURPOSE = 'complete-profile';

function getSecret(): string {
  return process.env.OPT_OUT_HMAC_SECRET ?? process.env.CRON_SECRET ?? '';
}

/** Build the HMAC token for a lead's complete-profile link. */
export function buildCompleteProfileToken(leadId: string): string {
  return createHmac('sha256', getSecret())
    .update(`${leadId}:${PURPOSE}`)
    .digest('hex');
}

/**
 * Constant-time verification of a complete-profile token for a lead. Returns
 * false on any mismatch (including length mismatch, which `timingSafeEqual`
 * would otherwise throw on).
 */
export function verifyCompleteProfileToken(
  leadId: string,
  token: string,
): boolean {
  const expected = buildCompleteProfileToken(leadId);
  const a = Buffer.from(token);
  const b = Buffer.from(expected);
  if (a.length !== b.length) return false;
  try {
    return timingSafeEqual(a, b);
  } catch {
    return false;
  }
}
